import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import {
  EstimateInvalidError,
  EstimateRefusedError,
  useEstimateLink,
  type Estimate,
} from '@/routes/estimates/useEstimates';

/**
 * The statuses the API hands a link out in: sent, or answered (within 90 days,
 * which only the server can judge — past that, Copy shows its sentence). A
 * draft has no link yet; an expired or superseded estimate's link shows the
 * customer a page with no price, and the office has nothing to copy.
 */
export const LINK_STATUSES: ReadonlySet<string> = new Set(['Sent', 'Approved', 'Declined']);

/**
 * The office's handle on the customer's link (M2-07, pass B; Kyle,
 * 2026-09-27): Copy puts the full URL on the clipboard — the live link, or a
 * new one after a revoke — and Revoke, after a confirm, kills the link the
 * customer has. `customers.manage` only; the caller renders this only then,
 * and only in {@link LINK_STATUSES}. The server still decides, and a refused
 * copy shows its own sentence.
 *
 * The URL is also shown in a read-only box once copied, so it can be copied
 * by hand where the clipboard is not available (an insecure origin, a
 * browser that refuses).
 */
export function CustomerLink({ estimate }: { estimate: Estimate }) {
  const { t } = useTranslation();
  const id = useId();
  const { copy, revoke } = useEstimateLink(estimate.id);
  const [url, setUrl] = useState<string | null>(null);
  const [clipboardFailed, setClipboardFailed] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);

  const pending = copy.isPending || revoke.isPending;

  function explain(error: unknown) {
    setProblem(
      error instanceof EstimateRefusedError || error instanceof EstimateInvalidError
        ? t('estimates.acts.refused', { reason: error.message })
        : t('estimates.acts.failed'),
    );
  }

  function onCopy() {
    setProblem(null);
    setAnnouncement('');
    copy.mutate(undefined, {
      onSuccess: async (link) => {
        setUrl(link);
        try {
          await navigator.clipboard.writeText(link);
          setClipboardFailed(false);
          setAnnouncement(t('estimates.link.copied'));
        } catch {
          // Announced as well as shown: a screen-reader user otherwise hears
          // nothing, and cannot tell a copy that failed from one that worked
          // (found in review).
          setClipboardFailed(true);
          setAnnouncement(t('estimates.link.copyManually'));
        }
      },
      onError: explain,
    });
  }

  function onRevoke() {
    setProblem(null);
    revoke.mutate(undefined, {
      onSuccess: () => {
        setUrl(null);
        setClipboardFailed(false);
        setConfirmingRevoke(false);
        setAnnouncement(t('estimates.link.revoked'));
      },
      onError: explain,
    });
  }

  return (
    <section
      aria-labelledby={`${id}-heading`}
      className="space-y-3 rounded-lg border border-[var(--color-border)] p-4"
    >
      <h2 id={`${id}-heading`} className="text-base font-semibold">
        {t('estimates.link.heading')}
      </h2>
      <p className="text-sm text-[var(--color-muted-foreground)]">{t('estimates.link.help')}</p>

      <div className="flex flex-wrap gap-2">
        <Button type="button" disabled={pending} onClick={onCopy}>
          {copy.isPending ? t('estimates.link.copying') : t('estimates.link.copy')}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={pending}
          onClick={() => {
            setProblem(null);
            setConfirmingRevoke(true);
          }}
        >
          {t('estimates.link.revoke')}
        </Button>
      </div>

      {url ? (
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('estimates.link.url')}</span>
          <input
            readOnly
            className={FIELD_CLASS}
            value={url}
            onFocus={(event) => event.currentTarget.select()}
          />
        </label>
      ) : null}
      {clipboardFailed ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {t('estimates.link.copyManually')}
        </p>
      ) : null}

      {problem && !confirmingRevoke ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {problem}
        </p>
      ) : null}

      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>

      {confirmingRevoke ? (
        <ConfirmDialog
          titleId={`${id}-revoke-title`}
          title={t('estimates.link.revokeTitle')}
          description={t('estimates.link.revokeDescription')}
          confirmLabel={t('estimates.link.revokeConfirm')}
          destructive
          isPending={revoke.isPending}
          errorMessage={problem ?? undefined}
          onConfirm={onRevoke}
          onClose={() => setConfirmingRevoke(false)}
        />
      ) : null}
    </section>
  );
}
